import AdminShell from "../components/admin/AdminShell";
import { RevenuePanel } from "../components/admin/RevenuePanel";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import PushHealthPanel from "../components/admin/PushHealthPanel";
import { toast } from "sonner";
import { 
  Users, CreditCard, QrCode, LogOut, 
  BarChart3, Clock, Eye, Settings, ChevronRight,
  Phone, MessageCircle, Mail, Activity, RefreshCw, HeartPulse, Car
} from "lucide-react";

const API = '/api';

// Clickable stat card component - defined outside to avoid re-renders
const StatCard = ({ icon: Icon, iconBg, value, label, onClick, testId }) => (
  <Card 
    className="bg-[#14375F] border-white/15 cursor-pointer hover:bg-slate-750 hover:border-slate-600 transition-all" 
    onClick={onClick}
    data-testid={testId}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500" />
      </div>
      <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {value}
      </p>
      <p className="text-sm text-slate-400">{label}</p>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [counterCfg, setCounterCfg] = useState(null);
  const [autoRevival, setAutoRevival] = useState(null);
  const [autoRevivalRunning, setAutoRevivalRunning] = useState(false);
  const [callHealth, setCallHealth] = useState(null);
  const [callHealthDays, setCallHealthDays] = useState(7);

  const fetchCallHealth = async (days = callHealthDays) => {
    try {
      const r = await axios.get(`${API}/admin/call-health/summary`, { params: { days } });
      setCallHealth(r.data);
    } catch (_e) { /* card shows fallback */ }
  };

  const fetchAutoRevival = async () => {
    try {
      const r = await axios.get(`${API}/admin/auto-revival/status`);
      setAutoRevival(r.data);
    } catch (_) { /* opcional */ }
  };

  const handleAutoRevivalRunNow = async () => {
    setAutoRevivalRunning(true);
    try {
      const r = await axios.post(`${API}/admin/auto-revival/run-now`);
      const s = r.data?.summary || {};
      toast.success(`Barrido OK · ${s.dead_users} muertos · ${s.push_sent} push · ${s.rate_limited} throttled`);
      await fetchAutoRevival();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 429 && d?.retry_in_seconds != null) {
        toast.error(`Throttle: reintenta en ${d.retry_in_seconds}s`);
      } else {
        toast.error("Error al ejecutar auto-revival");
      }
    } finally {
      setAutoRevivalRunning(false);
    }
  };

  const fetchCounterCfg = async () => {
    try {
      const r = await axios.get(`${API}/admin/public-counter`);
      setCounterCfg(r.data);
    } catch (_) { /* opcional */ }
  };

  const toggleCounter = async () => {
    try {
      const next = !counterCfg?.show_public_counter;
      await axios.put(`${API}/admin/public-counter`, { show_public_counter: next });
      setCounterCfg({ ...counterCfg, show_public_counter: next });
      toast.success(next ? "Contador público ACTIVADO en la landing" : "Contador público desactivado");
    } catch (_) {
      toast.error("Error al cambiar el contador");
    }
  };

  useEffect(() => {
    fetchStats();
    fetchLogs();
    fetchCounterCfg();
    fetchAutoRevival();
    fetchCallHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { params: { hide_test: "1" } });
      setStats(response.data);
      try {
        const ov = await axios.get(`${API}/admin/uc/overview`, { params: { hide_test: "1" } });
        setOverview(ov.data);
      } catch (_) { /* overview opcional */ }
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = logFilter !== "all" ? { log_type: logFilter } : {};
      const response = await axios.get(`${API}/admin/logs`, { params });
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStats();
    await fetchLogs();
    await fetchAutoRevival();
    setRefreshing(false);
    toast.success("Datos actualizados");
  };

  const getLogIcon = (type) => {
    switch (type) {
      case "call": return <Phone className="w-4 h-4 text-[#5BC0FF]" />;
      case "whatsapp": return <MessageCircle className="w-4 h-4 text-green-400" />;
      case "message": return <Mail className="w-4 h-4 text-sky-400" />;
      case "admin": return <Settings className="w-4 h-4 text-amber-400" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <AdminShell
      testid="admin-dashboard"
      title="Dashboard"
      description="Vista general del negocio AvisoCar: clientes, pagos, membresías y salud de avisos."
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="border-white/15 text-slate-300 hover:bg-white/10"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      }
    >
      <div>
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <>
              {/* MASTER ADMIN V2 §1 — ingresos reales, solo pagos aprobados */}
              <div className="mb-6">
                <RevenuePanel hideTest />
              </div>

              {/* iter63 3.2: Overview operativo */}
              {overview && (
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-4" data-testid="admin-overview-cards">
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4" data-testid="ov-subs">
                    <p className="text-slate-400 text-xs mb-2">Suscripciones por estado</p>
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-[#1B84FF]/20 text-emerald-300">Activas {overview.subs_by_status?.active || 0}</span>
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Pend. {overview.subs_by_status?.pending_payment || 0}</span>
                      <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">Venc. {overview.subs_by_status?.expired || 0}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-300">Standby {overview.subs_by_status?.standby || 0}</span>
                    </div>
                  </div>
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4 cursor-pointer hover:border-amber-500/40" onClick={() => navigate('/admin/users?expiring=1')} data-testid="ov-expiring">
                    <p className="text-slate-400 text-xs mb-1">Expiran en 30 días</p>
                    <p className="text-2xl font-bold text-amber-300">{overview.expiring_30d}</p>
                  </div>
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4 cursor-pointer hover:border-red-500/40" onClick={() => navigate('/admin/users?operational=yellow')} data-testid="ov-active-no-push">
                    <p className="text-slate-400 text-xs mb-1">Activas SIN push</p>
                    <p className="text-2xl font-bold text-red-300">{overview.active_sub_no_push ?? 0}</p>
                    <p className="text-[10px] text-slate-500">No recibirán avisos → filtro 🟡</p>
                  </div>
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4 cursor-pointer hover:border-orange-500/40" onClick={() => navigate('/admin/users?push=stale')} data-testid="ov-stale-devices">
                    <p className="text-slate-400 text-xs mb-1">Dispositivos sin vida &gt;48h</p>
                    <p className="text-2xl font-bold text-orange-300">{overview.stale_devices_48h ?? 0}</p>
                    <p className="text-[10px] text-slate-500">Push moribundo: reenvía la guía</p>
                  </div>
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4 cursor-pointer hover:border-red-500/40" onClick={() => navigate('/admin/payments')} data-testid="ov-suspicious-payments">
                    <p className="text-slate-400 text-xs mb-1">Pagos sospechosos</p>
                    <p className="text-2xl font-bold text-red-300">{overview.suspicious_payments ?? 0}</p>
                    <p className="text-[10px] text-slate-500">⚠️ país no coincide · {overview.suspicious_pending ?? 0} pendientes</p>
                  </div>
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4" data-testid="ov-push-today">
                    <p className="text-slate-400 text-xs mb-1">Pushes hoy</p>
                    <p className="text-2xl font-bold text-white">
                      <span className="text-[#5BC0FF]">{overview.push_sent_today}</span>
                      <span className="text-slate-600 text-base"> ok / </span>
                      <span className="text-red-400">{overview.push_failed_today}</span>
                      <span className="text-slate-600 text-base"> fallo</span>
                    </p>
                  </div>
                  <div className="bg-[#0B2545] border border-white/10 rounded-xl p-4" data-testid="ov-qr-today">
                    <p className="text-slate-400 text-xs mb-1">Escaneos QR hoy</p>
                    <p className="text-2xl font-bold text-white">{overview.qr_views_today}</p>
                  </div>
                </div>
              )}

              {/* Stats Grid - Row 1: Users & Vehicles */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <StatCard 
                  icon={Users}
                  iconBg="bg-sky-500/20 text-sky-400"
                  value={stats?.total_users || 0}
                  label="Usuarios Totales"
                  onClick={() => navigate('/admin/users')}
                  testId="stat-users"
                />
                <StatCard 
                  icon={Car}
                  iconBg="bg-[#1B84FF]/20 text-[#5BC0FF]"
                  value={stats?.total_vehicles || 0}
                  label="Vehículos"
                  onClick={() => navigate('/admin/vehicles')}
                  testId="stat-vehicles"
                />
                <StatCard 
                  icon={QrCode}
                  iconBg="bg-purple-500/20 text-purple-400"
                  value={stats?.active_qrs || 0}
                  label="QRs Activos"
                  onClick={() => navigate('/admin/vehicles?qr_status=active')}
                  testId="stat-active-qrs"
                />
                <StatCard 
                  icon={Clock}
                  iconBg="bg-amber-500/20 text-amber-400"
                  value={stats?.pending_payments || 0}
                  label="Pagos Pendientes"
                  onClick={() => navigate('/admin/payments?tab=pending')}
                  testId="stat-pending-payments"
                />
              </div>

              {/* Stats Grid - Row 2: Communication Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard 
                  icon={Eye}
                  iconBg="bg-slate-500/20 text-slate-400"
                  value={stats?.total_scans || 0}
                  label="Escaneos QR"
                  onClick={() => navigate('/admin/logs?type=scan')}
                  testId="stat-scans"
                />
                <StatCard 
                  icon={Phone}
                  iconBg="bg-[#1B84FF]/20 text-[#5BC0FF]"
                  value={stats?.total_calls || 0}
                  label="Llamadas privadas"
                  onClick={() => navigate('/admin/logs?type=calls')}
                  testId="stat-calls"
                />
                {/* iter64 — 'WhatsApp Anónimos' métrica: legacy Twilio dead. Renombrado a "Clicks Soporte WhatsApp" para reflejar la realidad (clicks a wa.me), pendiente de eliminarse cuando se implemente tracking real. */}
                <StatCard 
                  icon={MessageCircle}
                  iconBg="bg-green-500/20 text-green-400"
                  value={stats?.total_whatsapp || 0}
                  label="Clicks soporte WhatsApp"
                  onClick={() => navigate('/admin/logs?type=whatsapp')}
                  testId="stat-whatsapp"
                />
                <StatCard 
                  icon={Mail}
                  iconBg="bg-sky-500/20 text-sky-400"
                  value={stats?.total_messages || 0}
                  label="Mensajes Internos"
                  onClick={() => navigate('/admin/logs?type=messages')}
                  testId="stat-messages"
                />
              </div>

              {/* Runtime bracket-tag log shortcut */}
              <div className="mb-6">
                <button
                  onClick={() => navigate('/admin/runtime-logs')}
                  className="w-full text-left rounded-xl border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/15 transition-colors p-4 flex items-center gap-3"
                  data-testid="nav-runtime-logs"
                >
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-mono font-bold">[]</div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-cyan-200">Runtime Logs [BRACKET_TAGS]</div>
                    <div className="text-xs text-cyan-300/70">Verdad en vivo del pipeline mensaje + llamada (auto-refresh 5s)</div>
                  </div>
                </button>
              </div>

              {/* iter67 F5 — toggle contador público (default OFF) */}
              {counterCfg && (
                <div className="mb-6 rounded-xl border border-white/10 bg-[#0B2545] p-4 flex items-center justify-between gap-3" data-testid="public-counter-toggle-card">
                  <div>
                    <p className="text-sm font-bold text-white">Contador público en la landing</p>
                    <p className="text-xs text-slate-500">
                      {counterCfg.alerts_delivered?.toLocaleString()} avisos reales entregados (excluye pruebas y cuentas test). Apagado por defecto.
                    </p>
                  </div>
                  <button
                    onClick={toggleCounter}
                    className={`relative w-12 h-6 rounded-full transition-colors ${counterCfg.show_public_counter ? "bg-[#1B84FF]" : "bg-slate-700"}`}
                    data-testid="public-counter-toggle"
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${counterCfg.show_public_counter ? "left-6" : "left-0.5"}`} />
                  </button>
                </div>
              )}

              {/* Auto-Revival Health (iter70) */}
              <Card className="bg-[#14375F] border-white/15" data-testid="auto-revival-health-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <HeartPulse className="w-5 h-5 text-rose-400" />
                    Salud del Auto-Revival
                  </CardTitle>
                  <Button
                    onClick={handleAutoRevivalRunNow}
                    disabled={autoRevivalRunning}
                    className="bg-rose-500 hover:bg-rose-600 text-white h-8 text-xs"
                    data-testid="auto-revival-run-now-btn"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${autoRevivalRunning ? "animate-spin" : ""}`} />
                    {autoRevivalRunning ? "Ejecutando…" : "Ejecutar ahora"}
                  </Button>
                </CardHeader>
                <CardContent>
                  {!autoRevival ? (
                    <p className="text-slate-500 text-sm">Cargando…</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500 text-xs">Muertos ahora (&gt;{autoRevival.hours_threshold}h)</p>
                        <p className="text-2xl font-bold text-rose-400" data-testid="auto-revival-dead-now">
                          {autoRevival.dead_users_now ?? 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Última corrida</p>
                        <p className="text-white text-sm" data-testid="auto-revival-last-run">
                          {autoRevival.last_run_at
                            ? new Date(autoRevival.last_run_at).toLocaleString('es-PE')
                            : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Triggered / Push enviados</p>
                        <p className="text-white text-sm">
                          {(autoRevival.last_summary?.triggered ?? 0)} / <span className="text-[#5BC0FF]">{autoRevival.last_summary?.push_sent ?? 0}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Rate-limited / Errores</p>
                        <p className="text-white text-sm">
                          {(autoRevival.last_summary?.rate_limited ?? 0)} / <span className="text-amber-400">{autoRevival.last_summary?.errors ?? 0}</span>
                        </p>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-3">
                    Barrido automático semanal · rate-limit 1 push/usuario/24h · el botón manual se puede repetir cada 5min.
                  </p>
                </CardContent>
              </Card>

              {/* iter71+ — Salud de llamadas */}
              <Card className="bg-[#14375F] border-white/15 mt-6" data-testid="call-health-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Phone className="w-5 h-5 text-[#5BC0FF]" />
                    Salud de llamadas
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <select
                      value={callHealthDays}
                      onChange={(e) => { const d = Number(e.target.value); setCallHealthDays(d); fetchCallHealth(d); }}
                      className="bg-[#0B2545] border border-white/15 text-slate-300 text-xs rounded-lg px-2 py-1.5"
                      data-testid="call-health-days-select"
                    >
                      <option value={7}>7 días</option>
                      <option value={30}>30 días</option>
                      <option value={90}>90 días</option>
                    </select>
                    <Button
                      onClick={() => fetchCallHealth()}
                      className="bg-[#1B84FF] hover:bg-[#1B84FF] text-white h-8 text-xs"
                      data-testid="call-health-refresh-btn"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!callHealth ? (
                    <p className="text-slate-500 text-sm">Cargando…</p>
                  ) : (
                    <>
                      {callHealth.turn_fallback_high_events > 0 && (
                        <div className="mb-3 text-xs bg-rose-500/10 border border-rose-500/40 text-rose-200 rounded px-3 py-2" data-testid="turn-fallback-alert">
                          ⚠ [TURN_FALLBACK_ACTIVE] — el TURN primario (Cloudflare) falló {callHealth.turn_fallback_high_events} vez/veces en la ventana; las llamadas usaron el respaldo Open Relay.
                          {callHealth.turn_fallback_last_at && <span className="text-rose-300/70"> Último: {new Date(callHealth.turn_fallback_last_at).toLocaleString("es-PE")}</span>}
                        </div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                        <div>
                          <p className="text-slate-500 text-xs">Tasa de conexión</p>
                          <p className={`text-2xl font-bold ${callHealth.connection_rate_pct === null ? "text-slate-500" : callHealth.connection_rate_pct >= 80 ? "text-[#5BC0FF]" : callHealth.connection_rate_pct >= 50 ? "text-amber-400" : "text-rose-400"}`} data-testid="call-health-rate">
                            {callHealth.connection_rate_pct === null ? "—" : `${callHealth.connection_rate_pct}%`}
                          </p>
                          <p className="text-[10px] text-slate-500">{callHealth.connected_calls}/{callHealth.accepted_calls} aceptadas conectaron</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Camino ICE usado</p>
                          <div className="flex flex-wrap gap-1 mt-1" data-testid="call-health-paths">
                            {Object.keys(callHealth.path_breakdown || {}).length === 0 && <span className="text-slate-500 text-xs">—</span>}
                            {Object.entries(callHealth.path_breakdown || {}).map(([p, n]) => (
                              <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full border ${p === "relay" ? "border-sky-400/50 text-sky-300 bg-sky-500/10" : p === "srflx" ? "border-violet-400/50 text-violet-300 bg-violet-500/10" : "border-emerald-400/50 text-emerald-300 bg-[#1B84FF]/10"}`}>
                                {p} × {n}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Timeouts / ICE restarts</p>
                          <p className="text-white text-sm mt-1">
                            <span className="text-amber-400 font-bold">{callHealth.timeout_sessions}</span> / {callHealth.ice_restarts}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Hangups zombis descartados</p>
                          <p className="text-white text-sm mt-1 font-bold">{callHealth.stale_hangups_dropped}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">% llamadas relayadas (TURN)</p>
                          <p className="text-2xl font-bold text-sky-400" data-testid="call-health-relay-pct">
                            {callHealth.relay_pct === null || callHealth.relay_pct === undefined ? "—" : `${callHealth.relay_pct}%`}
                          </p>
                          <p className="text-[10px] text-slate-500">{callHealth.relayed_calls ?? 0} relayadas · {callHealth.relay_minutes ?? 0} min relay</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Duración media</p>
                          <p className="text-white text-sm mt-1 font-bold" data-testid="call-health-avg-duration">
                            {callHealth.avg_duration_sec == null ? "—" : `${Math.floor(callHealth.avg_duration_sec / 60)}m ${callHealth.avg_duration_sec % 60}s`}
                          </p>
                          <p className="text-[10px] text-slate-500">{callHealth.total_call_minutes ?? 0} min totales</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">GB relay estimados / mes</p>
                          <p className="text-white text-sm mt-1 font-bold" data-testid="call-health-gb-month">
                            {callHealth.est_relay_gb_month ?? 0} GB
                          </p>
                          <p className="text-[10px] text-slate-500">proyección 30 días · Cloudflare gratis hasta 1.000 GB</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Proveedor TURN usado</p>
                          <div className="flex flex-wrap gap-1 mt-1" data-testid="call-health-providers">
                            {Object.keys(callHealth.provider_breakdown || {}).length === 0 && <span className="text-slate-500 text-xs">—</span>}
                            {Object.entries(callHealth.provider_breakdown || {}).map(([p, n]) => (
                              <span key={p} className={`text-[10px] px-2 py-0.5 rounded-full border ${p === "cloudflare" ? "border-orange-400/50 text-orange-300 bg-orange-500/10" : p === "open_relay" ? "border-rose-400/50 text-rose-300 bg-rose-500/10" : "border-slate-500/50 text-slate-300 bg-slate-500/10"}`}>
                                {p} × {n}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      {(callHealth.recent_calls || []).length > 0 && (
                        <div className="border-t border-white/15 pt-3">
                          <p className="text-xs text-slate-500 mb-2">Últimas llamadas aceptadas</p>
                          <div className="space-y-1.5" data-testid="call-health-recent">
                            {callHealth.recent_calls.map((c) => (
                              <div key={c.session_id} className="flex items-center gap-2 text-xs">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${c.outcome === "connected" ? "bg-emerald-400" : c.outcome === "timeout" ? "bg-rose-400" : "bg-slate-500"}`} />
                                <span className="font-mono text-slate-400">{c.session_short}</span>
                                <span className={c.outcome === "connected" ? "text-emerald-300" : c.outcome === "timeout" ? "text-rose-300" : "text-slate-400"}>
                                  {c.outcome === "connected" ? "conectó" : c.outcome === "timeout" ? "timeout" : "no conectó"}
                                </span>
                                {Object.entries(c.paths || {}).map(([role, p]) => (
                                  <span key={role} className="text-[10px] text-slate-500">{role}:{p}</span>
                                ))}
                                {c.duration_sec != null && <span className="text-[10px] text-sky-300/80">{Math.floor(c.duration_sec / 60)}m{c.duration_sec % 60}s</span>}
                                {c.provider && <span className="text-[10px] text-orange-300/70">{c.provider}</span>}
                                {c.attempts > 1 && <span className="text-[10px] text-amber-400/80">×{c.attempts} intentos</span>}
                                <span className="ml-auto text-[10px] text-slate-600">
                                  {c.accepted_at ? new Date(c.accepted_at).toLocaleString("es-PE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="text-[10px] text-slate-500 mt-3">
                        Conectada = pc connected reportado por el navegador ([ICE_PATH_USED]). Excluye sesiones de test.
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Bloque 1 — vistas diferidas Corte 6 (A1/A4/A5/A6) */}
              <PushHealthPanel />

              {/* Quick Actions & Logs */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <Card className="bg-[#14375F] border-white/15">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-amber-400" />
                      Pagos Pendientes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 mb-4">
                      {stats?.pending_payments > 0 
                        ? `Tienes ${stats.pending_payments} pago(s) pendiente(s) de revisión`
                        : "No hay pagos pendientes de revisión"
                      }
                    </p>
                    <Link to="/admin/payments?tab=pending">
                      <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="go-to-payments-btn">
                        <Eye className="w-4 h-4 mr-2" />
                        Revisar Pagos
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                {/* Activity Logs */}
                <Card className="bg-[#14375F] border-white/15">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-[#5BC0FF]" />
                      Actividad Reciente
                    </CardTitle>
                    <Select value={logFilter} onValueChange={setLogFilter}>
                      <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white text-sm h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="calls">Llamadas</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="messages">Mensajes</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {logs.length === 0 ? (
                        <p className="text-slate-500 text-sm">No hay actividad reciente</p>
                      ) : (
                        logs.slice(0, 10).map((log, index) => (
                          <div key={log.id || index} className="flex items-center gap-3 p-2 bg-slate-700/50 rounded-lg">
                            {getLogIcon(log.type)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{log.action}</p>
                              <p className="text-xs text-slate-500">
                                {new Date(log.created_at).toLocaleString('es-PE')}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
      </div>
    </AdminShell>
  );
};

export default AdminDashboard;
